import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk } from '../../../store';
import axios from '../../../axios';
import { IconType } from '../../../shared/models/iconType.enum';
import { ToastAnt } from '../../Shared/Toast/ToastAnt';
import { SelectOption } from '../Annotation/annotation.types';

type AssessmentLandingQuickFiltersRequest = {
  findingTypeName: string[];
};

type AssessmentLandingQuickFiltersResponse = {
  findingTypeName: SelectOption[];
};

type PaggingConfiguration = {
  take: number;
  skip: number;
  getTotal: boolean;
  filters: AssessmentLandingQuickFiltersRequest;
};

type PaggingResult = {
  totalNumber?: number;
  elements: FindingTypeTemplateType[];
  filters: AssessmentLandingQuickFiltersResponse;
};

export type FindingTypeTemplateType = {
  id: string | null;
  name: string;
  version: string;
  description: string;
  icon: IconType | null;
  color: string;
  propertyIds: Array<string>;
  properties: Array<FindingTypeProperty>;
  propertyTemplateIdsToAdd: Array<string>;
  propertyIdsToRemove: Array<string>;
  findingTypeIds: Array<string>;
  canDelete: boolean;
  isMLRelated: boolean;
};

export type FindingTypeProperty = {
  id: string;
  templateId: string;
  description: string;
  childProperties: Array<string>;
};

type State = {
  findingTypeTemplates: Array<FindingTypeTemplateType>;
  selectedFindingTypeTemplate: FindingTypeTemplateType;
  loading: boolean;
  modalVisible: boolean;
  findingTypeNames: SelectOption[];
  selectedFindingTypeNames: string[];
  totalNumber: number;
  currentPage: number;
  size: number;
};

const initialFindingTypeTemplate: FindingTypeTemplateType = {
  id: '',
  name: '',
  description: '',
  version: '',
  color: '',
  icon: null,
  propertyIds: [],
  propertyIdsToRemove: [],
  propertyTemplateIdsToAdd: [],
  properties: [],
  findingTypeIds: [],
  canDelete: false,
  isMLRelated: false
};

export const take = 10;

const initialState: State = {
  findingTypeTemplates: Array<FindingTypeTemplateType>(),
  selectedFindingTypeTemplate: initialFindingTypeTemplate,
  loading: false,
  modalVisible: false,
  findingTypeNames: [],
  selectedFindingTypeNames: [],
  totalNumber: 0,
  currentPage: 0,
  size: take
};

const findingTypeTemplateSlice = createSlice({
  name: 'findingTypeTemplate',
  initialState,
  reducers: {
    fetchFindingTypeTemplatesStarted(state) {
      state.loading = true;
    },
    fetchFindingTypeTemplatesSuccess(state, action: PayloadAction<PaggingResult>) {
      const { elements, filters, totalNumber } = action.payload;
      state.findingTypeTemplates = elements;
      state.findingTypeNames = filters.findingTypeName;

      if (totalNumber) {
        state.totalNumber = totalNumber;
      }

      state.loading = false;
    },
    fetchFindingTypeTemplatesFailed(state) {
      state.loading = false;
    },
    getFindingTypeTemplateStarted(state) {
      state.loading = true;
    },
    getFindingTypeTemplateSuccess(state, action: PayloadAction<FindingTypeTemplateType>) {
      state.selectedFindingTypeTemplate = action.payload;
      state.loading = false;
    },
    getFindingTypeTemplateFailed(state) {
      state.loading = false;
    },
    showModal(state, action: PayloadAction<boolean>) {
      state.modalVisible = action.payload;
    },
    setSizeAndPage: (state, action: PayloadAction<{ size: number; page: number }>) => {
      const { page, size } = action.payload;
      state.size = size;
      state.currentPage = page;
    },
    setSelectedFindingTypeNames(state, action: PayloadAction<string | undefined>) {
      state.selectedFindingTypeNames = action.payload ? [action.payload] : [];
    }
  }
});

export const {
  fetchFindingTypeTemplatesStarted,
  fetchFindingTypeTemplatesSuccess,
  fetchFindingTypeTemplatesFailed,
  getFindingTypeTemplateStarted,
  getFindingTypeTemplateSuccess,
  getFindingTypeTemplateFailed,
  showModal,
  setSizeAndPage,
  setSelectedFindingTypeNames
} = findingTypeTemplateSlice.actions;

export const fetchFindingTypeTemplates =
  (take: number, newPage: number): AppThunk =>
  async (dispatch, getState) => {
    const { selectedFindingTypeNames } = getState().findingTypeTemplate;

    dispatch(fetchFindingTypeTemplatesStarted());
    dispatch(setSizeAndPage({ page: newPage, size: take }));

    const config: PaggingConfiguration = {
      take: take,
      skip: (newPage - 1) * take,
      getTotal: true,
      filters: {
        findingTypeName: selectedFindingTypeNames
      }
    };

    try {
      const response = await axios.post<PaggingResult>(`/findingTypeTemplates/search`, config);
      dispatch(fetchFindingTypeTemplatesSuccess(response.data));
    } catch {
      dispatch(fetchFindingTypeTemplatesFailed());
    }
  };

export const getFindingTypeTemplate =
  (findingTypeTemplateId: string): AppThunk =>
  async dispatch => {
    try {
      dispatch(getFindingTypeTemplateStarted);
      if (findingTypeTemplateId) {
        const response = await axios.get(`/findingTypeTemplates/${findingTypeTemplateId}`);
        dispatch(getFindingTypeTemplateSuccess(response.data));
      } else {
        dispatch(getFindingTypeTemplateSuccess(initialFindingTypeTemplate));
      }
    } catch {
      dispatch(getFindingTypeTemplateFailed);
    }
  };

export const saveFindingTypeTemplate =
  (findingTypeTemplate: FindingTypeTemplateType): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().findingTypeTemplate;
    try {
      if (findingTypeTemplate.id) {
        await axios.put(`/findingTypeTemplates`, {
          id: findingTypeTemplate.id,
          name: findingTypeTemplate.name,
          version: findingTypeTemplate.version,
          description: findingTypeTemplate.description,
          icon: IconType[findingTypeTemplate.icon!],
          color: findingTypeTemplate.color,
          propertyTemplateIdsToAdd: findingTypeTemplate.propertyTemplateIdsToAdd,
          propertyIdsToRemove: findingTypeTemplate.propertyIdsToRemove,
          isMLRelated: findingTypeTemplate.isMLRelated
        });
      } else {
        await axios.post(`/findingTypeTemplates`, {
          name: findingTypeTemplate.name,
          version: findingTypeTemplate.version,
          description: findingTypeTemplate.description,
          icon: IconType[findingTypeTemplate.icon!],
          color: findingTypeTemplate.color,
          propertyTemplateIdsToAdd: findingTypeTemplate.propertyTemplateIdsToAdd,
          propertyIdsToRemove: findingTypeTemplate.propertyIdsToRemove,
          isMLRelated: findingTypeTemplate.isMLRelated
        });
      }
      dispatch(fetchFindingTypeTemplates(size, 1));
      ToastAnt('Saved.');
    } catch (error: any) {
      ToastAnt(error?.response?.data);
    }
  };

export const deleteFindingTypeTemplate =
  (id: string): AppThunk =>
  async (dispatch, getState) => {
    const { size } = getState().findingTypeTemplate;

    try {
      await axios.delete(`/findingTypeTemplates/${id}`);
      dispatch(fetchFindingTypeTemplates(size, 1));
      ToastAnt('Deleted.');
    } catch (error) {
      ToastAnt('An error occured.');
    }
  };

export default findingTypeTemplateSlice.reducer;
